import type { QueryClient } from '@tanstack/react-query';

import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED } from '@/shared/realtime';
import type { ModuleSetupContext } from '@/shared/registry/types';

import { crmKeys, ENTITY_TYPES, invalidateActivities, invalidateRecord, invalidateViews, type EntityType } from './api';

interface RecordPayload {
  entityType: EntityType;
  id: string;
  actorId?: string | null;
  reason?: string;
  pipelineId?: string | null;
}

interface ImportProgressPayload {
  jobId: string;
  entityType: EntityType;
  status: string;
  processedRows: number;
  totalRows: number;
}

interface ActivityPayload {
  activityId: string;
  ownerType: EntityType;
  ownerId: string;
  reason?: string;
}

/**
 * Realtime events of the CRM (docs/modules/crm.md): everyone who may see a record receives
 * `crm.<type>-created|updated|deleted` (boards update live between users); an open card subscribes `crm:<type>:<id>`
 * for its timeline; activity changes refresh the lists and the sidebar counter; the owner of an import gets
 * `crm.import-progress`.
 */
export function setupCrmRealtime({ queryClient, realtime }: ModuleSetupContext): void {
  for (const type of ENTITY_TYPES) {
    const onChange = (payload: RecordPayload) => {
      if (!payload.id) return;
      invalidateRecord(queryClient, type, payload.id);
      if (payload.reason === 'converted') for (const other of ENTITY_TYPES) invalidateViews(queryClient, other);
      // A record back from the trash leaves it.
      if (payload.reason === 'restored') void queryClient.invalidateQueries({ queryKey: crmKeys.trashAll });
    };
    realtime.on<RecordPayload>(`crm.${type}-created`, onChange);
    realtime.on<RecordPayload>(`crm.${type}-updated`, onChange);
    realtime.on<RecordPayload>(`crm.${type}-deleted`, (payload) => {
      if (!payload.id) return;
      void queryClient.invalidateQueries({ queryKey: crmKeys.detail(type, payload.id) });
      invalidateViews(queryClient, type);
      // Deleted records go to the trash (or leave it for good).
      void queryClient.invalidateQueries({ queryKey: crmKeys.trashAll });
    });
  }
  // The entity types, the pipelines (their stage requirements, the pipeline matrix) or the access to them changed: menus,
  // forms and lists read the configuration again.
  realtime.on('crm.types-changed', () => {
    void queryClient.invalidateQueries({ queryKey: crmKeys.me });
    void queryClient.invalidateQueries({ queryKey: crmKeys.types });
    void queryClient.invalidateQueries({ queryKey: crmKeys.settings });
  });
  realtime.on<RecordPayload>('crm.timeline-updated', (payload) => {
    if (!payload.id) return;
    void queryClient.invalidateQueries({ queryKey: crmKeys.timeline(payload.entityType, payload.id) });
    void queryClient.invalidateQueries({ queryKey: crmKeys.detail(payload.entityType, payload.id) });
  });
  realtime.on<ActivityPayload>('crm.activity-updated', (payload) => {
    invalidateActivities(queryClient, payload.ownerId ? { type: payload.ownerType, id: payload.ownerId } : undefined);
    void queryClient.invalidateQueries({ queryKey: crmKeys.counters });
  });
  realtime.on<{ topic?: string } | null>(REALTIME_TOPIC_REVOKED, (payload) => onTopicRevoked(queryClient, payload?.topic));
  realtime.on<ImportProgressPayload>('crm.import-progress', (payload) => {
    if (!payload.jobId) return;
    void queryClient.invalidateQueries({ queryKey: crmKeys.importJob(payload.jobId) });
    void queryClient.invalidateQueries({ queryKey: crmKeys.imports, exact: true });
    if (payload.status === 'completed') invalidateViews(queryClient, payload.entityType);
  });
  realtime.on(REALTIME_RECONNECTED, () => void queryClient.invalidateQueries({ queryKey: crmKeys.all }));
}

/** The user lost access to a record (`crm:<type>:<id>`): the open card refetches into its "not found" state. */
export function onTopicRevoked(queryClient: QueryClient, topic: string | undefined): void {
  const match = /^crm:(lead|deal|contact|company|quote|invoice|item):(.+)$/u.exec(topic ?? '');
  if (!match) return;
  invalidateRecord(queryClient, match[1] as EntityType, match[2] ?? '');
}
